/**
 * Outbound clipboard fan-out (`broadcastClip`) + the inbound shape guard (`isClipboardTextPayload`).
 * `broadcastClip` mirrors the CLI `clipSend`: per-recipient encrypt inside the E2E envelope (server never sees content), `origin.surface="pwa"`; content arrives as an arg (no sync web clipboard-read). `isClipboardTextPayload` is used by `services/notifications.ts` ONLY — the SW uses `clipOriginLabel` instead to avoid importing `api/client` and breaking SW bundle isolation.
 * SECURITY: clip `content` is decrypted plaintext — never logged; the fan-out logs counts/deviceIds only.
 */

import type { ClipboardPayload, ClipboardTextPayload, SurfaceKind } from "@reflecto/core";
import { decodeBase64, encodeBase64, encryptMessage } from "@reflecto/core";
import { getDeviceKeys, sendClip } from "../api/client";
import { logWarn } from "../lib/log";

/** The closed `SurfaceKind` union, as a runtime Set for the shape guard. */
const SURFACE_KINDS = new Set<SurfaceKind>(["phone", "extension", "pwa", "cli", "desktop"]);

/** Per-leg network timeout for the clip fan-out (directory fetch + each send). */
const CLIP_FETCH_TIMEOUT_MS = 15_000;

/**
 * Runtime shape guard for a decrypted `ClipboardPayload`. Text-only until the
 * image-clip consumer slice — narrows to `ClipboardTextPayload` and rejects the
 * `contentType: "image"` variant for now. Validates the `type` + `contentType: "text"`
 * discriminants, the required string/number fields, the nested `origin`
 * object, and `provenance` (strictly one of `"device"` / `"cloud"`;
 * `undefined` and any unknown value are rejected). Loose only on
 * `sensitive` (optional boolean) — the goal is to reject non-clip
 * payloads, not to re-validate trusted fields a paired sender controls.
 *
 * Accepts `unknown` so callers can pass a freshly-`JSON.parse`d value
 * without a cast.
 */
export function isClipboardTextPayload(value: unknown): value is ClipboardTextPayload {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (v.type !== "clipboard") return false;
  if (v.contentType !== "text") return false;
  if (typeof v.content !== "string") return false;
  if (typeof v.timestamp !== "number") return false;
  if (v.sensitive !== undefined && typeof v.sensitive !== "boolean") return false;
  if (v.provenance !== "device" && v.provenance !== "cloud") return false;
  const origin = v.origin;
  if (typeof origin !== "object" || origin === null) return false;
  const o = origin as Record<string, unknown>;
  if (typeof o.label !== "string") return false;
  if (typeof o.surface !== "string" || !SURFACE_KINDS.has(o.surface as SurfaceKind)) return false;
  return true;
}

/** Per-call outcome so the manual-send UI can report partial / total failure. */
export interface BroadcastClipResult {
  /** Number of partner devices the clip reached. */
  sent: number;
  /** Total partner devices the directory listed. */
  total: number;
  /** Device ids that failed to encrypt-or-send. */
  failed: string[];
}

export interface BroadcastClipOptions {
  /** Display name of THIS device (origin.label). Falls back to "Web". */
  originLabel?: string;
  /** Mark the clip sensitive (preview-mask hint on the receiver). */
  sensitive?: boolean;
}

/**
 * Encrypt `content` per partner device and fan it out via
 * `sendClip` (`POST /v1/clipboard`). Returns a per-recipient outcome rather than
 * throwing on partial failure so the caller can surface "reached N/M".
 * Throws only when the prerequisites are missing (`not_paired`,
 * `empty`) or the directory fetch fails — i.e. cases where no send was
 * even attempted.
 *
 * `secretKey` is the caller's own base64 X25519 private key (read from
 * `webStorage` by the UI before calling, keeping this function free of
 * a storage dependency for testability).
 */
export async function broadcastClip(
  token: string | undefined,
  secretKeyB64: string | undefined,
  content: string,
  opts: BroadcastClipOptions = {}
): Promise<BroadcastClipResult> {
  if (!token || !secretKeyB64) {
    throw new Error("not_paired");
  }
  if (!content.trim()) {
    throw new Error("empty");
  }

  const surface: SurfaceKind = "pwa";
  const payload: ClipboardPayload = {
    type: "clipboard",
    contentType: "text",
    content,
    timestamp: Date.now(),
    origin: { label: opts.originLabel ?? "Web", surface },
    provenance: "device",
    ...(opts.sensitive ? { sensitive: true } : {}),
  };
  const plaintext = new TextEncoder().encode(JSON.stringify(payload));
  const secretKey = decodeBase64(secretKeyB64);

  // Bound every network leg so a hung connection can't stall the fan-out indefinitely.
  const { devices } = await getDeviceKeys(token, AbortSignal.timeout(CLIP_FETCH_TIMEOUT_MS));

  let sent = 0;
  const failed: string[] = [];
  for (const dev of devices) {
    try {
      const wire = encryptMessage(plaintext, decodeBase64(dev.publicKey), secretKey);
      await sendClip(
        token,
        encodeBase64(wire),
        crypto.randomUUID(),
        dev.deviceId,
        AbortSignal.timeout(CLIP_FETCH_TIMEOUT_MS)
      );
      sent++;
    } catch (err) {
      // Never log the clip content — only the device id + error.
      logWarn("clipboard", "clip_send_to_device_failed", err, { deviceId: dev.deviceId });
      failed.push(dev.deviceId);
    }
  }

  return { sent, total: devices.length, failed };
}
